/**
 * Keeps the browser tab level with the page, and lets a detail page say
 * whose record it is showing.
 *
 * See `utils/documentTitle` for what each route is called and why.
 */

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { titleForPath, titleForRecord } from '../utils/documentTitle'

/**
 * Sets the tab from the current route. Rendered once, above <Routes>, so
 * its effect lands before any page mounted underneath can override it.
 */
export function useRouteDocumentTitle(): void {
  const { pathname } = useLocation()
  useEffect(() => {
    const title = titleForPath(pathname)
    if (title) document.title = title
  }, [pathname])
}

/**
 * A detail page naming its record: `useDocumentTitle(client?.name)`.
 *
 * Pass nothing while the record is still loading and the generic route
 * title stays — "Client — Reppr" is true, and a placeholder name would not
 * be. There is no cleanup on purpose: the route effect above already sets
 * the next page's title on navigation, and restoring an old one here would
 * fight it.
 */
export function useDocumentTitle(name: string | null | undefined): void {
  useEffect(() => {
    if (!name) return
    document.title = titleForRecord(name)
  }, [name])
}
